import { useEffect, useRef } from 'react';
import { Avatar } from '@zep-us/zus/avatar';
import { Chip } from '@zep-us/zus/chip';
import { ICON_COLOR } from '@zep-us/zus/tokens';
import { useTranslation } from 'next-i18next';
import { type ClassGroupRes } from '@/api/gen/_model/classGroupRes';
import { ChevronRightIcon } from '@/icons';
import { useClassroomLinking } from '@/shared/features/classroom/hooks';
import { type ContentShareType } from '@/shared/features/classroom/types';
import { type ClassroomSource, trackClassroomEvent } from '@/shared/features/classroom/utils';
import { getClassroomAvatarColor, getDefaultClassroomBackgroundRef, toClassroomBackgroundRef } from '@/shared/features/classroom/utils/classroomAvatarColor';
import { cn } from '@/utils/tailwind.util';
import { SchoolGradientIcon } from './SchoolGradientIcon';
import { SHARE_ROW_CLASS_NAME } from './ShareActionList';

/** 연결된 클래스가 여럿이면 앞 몇 개까지 아바타를 겹쳐 보여 주는지. */
const MAX_STACKED_AVATARS = 2;

/**
 * 피그마 ShareClassRow: 연결된 클래스를 보여 주고, 누르면 가운데 「클래스 연결」 창을 연다.
 * 연결 전 → 학교 아이콘 + 「클래스 연결」, 연결됨 → 클래스 아바타 + 이름(여럿이면 「외 N」) + 「연결됨」.
 */
export function ShareClassRow({
  contentId,
  contentType,
  source,
  onClick
}: {
  contentId: string;
  contentType: ContentShareType;
  source: ClassroomSource;
  onClick: () => void;
}) {
  const {
    t
  } = useTranslation(['common']);
  const {
    classrooms,
    isLoadingClassrooms,
    isClassroomsError,
    isInitialized
  } = useClassroomLinking({
    contentId,
    contentType
  });
  const linkedClassrooms = classrooms.filter(c => c.isLinked);
  // 조회 실패면 「클래스 0개」로 집계하지 않는다.
  const isReady = isInitialized && !isLoadingClassrooms && !isClassroomsError;
  const hasTrackedViewRef = useRef(false);

  // 옛 공유 모달 클래스 섹션과 같은 노출 이벤트를 이어 쓴다 (집계 기준 유지).
  useEffect(() => {
    if (!isReady || hasTrackedViewRef.current) return;
    hasTrackedViewRef.current = true;
    trackClassroomEvent('view_link_to_classroom_section', {
      source,
      contentId,
      contentType,
      hasClassrooms: classrooms.length > 0,
      classroomCount: classrooms.length,
      linkedClassroomCount: linkedClassrooms.length
    });
  }, [classrooms.length, contentId, contentType, isReady, linkedClassrooms.length, source]);
  const [firstLinked] = linkedClassrooms;
  const otherCount = linkedClassrooms.length - 1;
  return <button type="button" className={cn(SHARE_ROW_CLASS_NAME, 'group')} onClick={() => {
    trackClassroomEvent('click_link_to_classroom', {
      source,
      contentId,
      contentType,
      linkedClassroomCount: linkedClassrooms.length
    });
    onClick();
  }} data-sentry-component="ShareClassRow" data-sentry-source-file="ShareClassRow.tsx">
      {firstLinked ? <>
          <span className="flex shrink-0 items-center">
            {linkedClassrooms.slice(0, MAX_STACKED_AVATARS).map((c, index) => <ClassroomAvatar key={c.classGroupId} classroom={c} className={index > 0 ? '-ml-xs' : undefined} />)}
          </span>
          {/* 이름이 길면 이름만 줄이고 「외 N」은 남긴다 — 다른 클래스에도 연결됐다는 정보라서. */}
          <span className="flex min-w-0 flex-1 gap-3xs text-body-lg-medium text-default">
            <span className="truncate">{firstLinked.name}</span>
            {otherCount > 0 && <span className="shrink-0">
                {t('common:global.andMoreCount', {
            n: otherCount
          })}
              </span>}
          </span>
          <Chip size="sm" rounded="full" className="shrink-0 border-green-100 bg-green-50 text-caption-md-semibold text-positive">
            {t('common:classroomLink.linkedChip')}
          </Chip>
        </> : <>
          <SchoolGradientIcon size="md" className="shrink-0" />
          <span className="min-w-0 flex-1 truncate text-body-lg-medium text-default">
            {t('common:classroom.linkToClassroom')}
          </span>
        </>}
      <ChevronRightIcon size="sm" fill={ICON_COLOR.alternative} data-sentry-element="ChevronRightIcon" data-sentry-source-file="ShareClassRow.tsx" />
    </button>;
}
function ClassroomAvatar({
  classroom,
  className
}: {
  classroom: ClassGroupRes;
  className?: string;
}) {
  // 클래스 목록·연결 창과 같은 색 규칙(저장 배경 → 클래스 id 기본값).
  const {
    avatarColor,
    avatarTextColor
  } = getClassroomAvatarColor(toClassroomBackgroundRef(classroom.background) ?? getDefaultClassroomBackgroundRef(classroom.classGroupId));
  return <Avatar size="xs" rounded="full" aria-label={classroom.name}
  // 클래스 색을 틀에도 칠한다. 안 그러면 ZUS 틀의 연보라 배경이 1px 테두리 자리에 비친다.
  style={{
    backgroundColor: avatarColor
  }}
  // 겹친 아바타 사이 테두리는 줄 배경색을 따라간다 (기본 흰색 → 호버 bg/neutral = gray-50).
  // ZUS 틀의 1px 테두리·연보라 안쪽 바탕은 지운다 — 남기면 원 가장자리에 옅은 선이 생겨 겹친 곳이 이중선으로 보인다.
  className={cn('border-0 ring-[3px] ring-white group-hover:ring-gray-50 [&>span]:bg-transparent', className)} fallback={<span className="flex size-full items-center justify-center overflow-hidden whitespace-nowrap text-body-md-semibold" style={{
    backgroundColor: avatarColor,
    color: avatarTextColor
  }}>
          {classroom.name.slice(0, 2)}
        </span>} data-sentry-element="Avatar" data-sentry-component="ClassroomAvatar" data-sentry-source-file="ShareClassRow.tsx" />;
}